import React, { useMemo } from "react";
import { matchPath } from "react-router-dom";

import { Roles } from "@/shared/utils/constants";
import { MenuItem } from "../types";
import { PrivateRoutes } from "@/router/Routes";
import HomeIcon from "@/assets/icons/home.svg?react";
import DashboardIcon from "@/assets/icons/home.svg?react";
import EvidenceRepositoryIcon from "@/assets/icons/evidence-repository.svg?react";
import OrganizationIcon from "@/assets/icons/organization-icon.svg?react";
import SurveyIcon from "@/assets/icons/survey-icon.svg?react";
import CommunityIcon from "@/assets/icons/community.svg?react";
import SendIcon from "@/assets/icons/send.svg?react";
import ShareIcon from "@/assets/icons/share.svg?react";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import { useTranslation } from "react-i18next";
import FolderIcon from "@/assets/icons/folder.svg?react";
import PaperClipIcon from "@/assets/icons/paper-clip.svg?react";
import { FullUserResponse } from "@/redux/users/types";

const useMenuItems = (currentUser: FullUserResponse | null) => {
  const { t } = useTranslation();

  return useMemo(() => {
    if (!currentUser) return [];

    const items: MenuItem[] = [
      {
        icon: <BaseSvgRenderer component={HomeIcon} width="20" height="20" />,
        key: "/",
        label: t("menu.home"),
      },
      {
        icon: (
          <BaseSvgRenderer component={DashboardIcon} width="20" height="20" />
        ),
        key: "/dashboard",
        label: t("menu.dashboard"),
      },
      {
        icon: (
          <BaseSvgRenderer
            component={EvidenceRepositoryIcon}
            width="20"
            height="20"
          />
        ),
        key: "/evidence",
        label: t("menu.evidence"),
        children: [
          {
            key: "/evidence/invites",
            icon: (
              <BaseSvgRenderer component={SendIcon} width="20" height="20" />
            ),
            label: t("menu.invites"),
          },
        ],
      },
      {
        key: "/files",
        icon: (
          <BaseSvgRenderer component={PaperClipIcon} width="20" height="20" />
        ),
        label: t("menu.files"),
      },
      ...(currentUser.organization
        ? [
            {
              icon: (
                <BaseSvgRenderer
                  component={OrganizationIcon}
                  width="20"
                  height="20"
                />
              ),
              key: `/signatories/${currentUser.organization.id}`,
              label: t("menu.organization"),
            },
          ]
        : []),
      {
        icon: <BaseSvgRenderer component={SurveyIcon} width="20" height="20" />,
        key: "/reports",
        label: t("menu.reports"),
        children: [
          {
            key: "/reports/views",
            icon: (
              <BaseSvgRenderer component={ShareIcon} width="20" height="20" />
            ),
            label: t("menu.publishAndShare"),
          },
        ],
      },
      {
        icon: (
          <BaseSvgRenderer component={CommunityIcon} width="20" height="20" />
        ),
        key: "/community",
        label: t("menu.community"),
        children: [
          {
            icon: (
              <BaseSvgRenderer component={FolderIcon} width="20" height="20" />
            ),
            key: "/directory",
            label: t("menu.directory"),
          },
        ],
      },
    ];

    return items.filter((item) => {
      if (!currentUser) return false;

      const route = PrivateRoutes.find((route) => {
        return matchPath({ path: route.path }, item.key) !== null;
      });
      if (!route || !route.allowedRoles) return false;

      if (currentUser.role === Roles.ADMIN.value) {
        return !(
          !route.allowedRoles || !route.allowedRoles.includes(Roles.ADMIN.value)
        );
      }

      if (currentUser.role === Roles.USER.value) {
        const hasAllowedUserRole =
          route.allowedRoles && route.allowedRoles.includes(Roles.USER.value);

        if (!hasAllowedUserRole) {
          return false;
        }
        return (
          currentUser.organizationRole &&
          route.allowedOrgRoles.includes(currentUser.organizationRole)
        );
      }
    });
  }, [currentUser]);
};

export default useMenuItems;
